import { Injectable } from '@angular/core';
import { Utils } from '../../../../../../common/js/Utils';
import { AmorphicService } from '../../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { BasicIllustrationService } from '../../../../../../coverpath-common/angular/services/basicillustration/BasicIllustrationService';
import { CoverpathUtils } from '../../../../../../coverpath-common/js/static/CoverpathUtils';
import { CoverpathCustomer } from '../../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { HtmlPdfController } from '../../../../../../coverpath-common/classes/wholelife/basicIllustration/pdf/HtmlPdfController';
import { CoverpathControllerInterface } from '../../../../../../coverpath-common/angular/interfaces/controller/CoverpathControllerInterface';
import { IllustrationRequest } from '../../../../../../coverpath-common/classes/wholelife/IllustrationRequest';
import { CoverpathBasePolicy } from '../../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathBasePolicy';
import { SafeInviteInfo } from '../../../../../../common/js/customer/SafeInviteInfo';
import { Controller } from '../../../Controller';
import { SafeWholePolicy } from '../../../../../../common/js/customer/SafePolicyInfo';

@Injectable()
export class QuoteCardService {
  private customer: CoverpathCustomer;
  private htmlPdfController: HtmlPdfController;
  private fetchingIllustration: boolean;
  public alirAmountAtIssue: number;
  public productDataLoaded;
  public safeInviteInfo: SafeInviteInfo;
  private controller: Controller;

  constructor(private amorphicService: AmorphicService, private basicIllustrationService: BasicIllustrationService) {
    this.customer = amorphicService.controller.customer as CoverpathCustomer;
    this.controller = amorphicService.controller as Controller;
    this.htmlPdfController = (this.amorphicService.controller as CoverpathControllerInterface).htmlPdfController;
    this.controller.inviteController.getSafeInviteInfo().then((safeInfo) => {
      this.safeInviteInfo = safeInfo;
      this.needsProductData().then((needsData) => {
        this.productDataLoaded = !needsData;
      });
    });
  }

  /**
   * Represents whether or not the current policy has enough data for initial render
   * or if it needs to asynchronously fetch more information
   */
  private async needsProductData(): Promise<boolean> {
    if (!!this.safeInviteInfo === false) {
      this.safeInviteInfo = await this.controller.inviteController.getSafeInviteInfo();
      if (!!this.safeInviteInfo === false) {
        return false;
      }
    }

    if (!!this.safeInviteInfo.safePolicyInfo.primaryPolicy === false) {
      return false;
    }

    return (this.safeInviteInfo.safePolicyInfo.primaryPolicy as SafeWholePolicy).hasALIR;
  }

  private async createBasicIllustrationRequest(isSupplemental: boolean = false): Promise<IllustrationRequest> {
    if (!!this.safeInviteInfo === false) {
      this.safeInviteInfo = await this.controller.inviteController.getSafeInviteInfo();
    }
    const customer: CoverpathCustomer = this.customer;
    return this.basicIllustrationService.createDefaultIllustrationRequest(
      customer,
      undefined,
      isSupplemental,
      this.safeInviteInfo
    );
  }

  /**
   * Generates a basic illustration, and opens it in a new tab
   *
   * @param controller the application controller
   * @param basicIllustrationService the basic illustration service
   *
   * @remarks keeps `this.fetchingIllustration` updated with status of this call
   */
  public async getBasicIllustrationPdf(isSupplemental: boolean = false) {
    this.fetchingIllustration = true;
    CoverpathUtils.dataLayerPush({
      event: 'vst-get-basicillustration',
    });
    const illustrationRequest = await this.createBasicIllustrationRequest(isSupplemental);
    this.basicIllustrationService
      .generateBasicIllustrationPDF(illustrationRequest, false)
      .then((response) => {
        if (response) {
          Utils.openFileInNewWindow(response, 'application/pdf');
        }
        this.fetchingIllustration = false;
      })
      .catch((e) => {
        this.fetchingIllustration = false;
      });
  }

  /**
   * Loads all of the data necessary for an initial render of the quote card.
   * Once finished, it sets a flag to deactivate the loading spinner
   */
  public async loadProductData() {
    if ((await this.needsProductData()) && !this.productDataLoaded) {
      const illustrationRequest = await this.createBasicIllustrationRequest();
      this.alirAmountAtIssue = await this.htmlPdfController.getAlirAmountAtIsssueFromIllustration(illustrationRequest);
      this.productDataLoaded = true;
    }
  }

  public getAlirAmountAtIssue(): number {
    return this.alirAmountAtIssue || 0;
  }
}
